import React, { Suspense, useCallback, useEffect, useState } from "react";
import ClientPortalNavbar from "./components/Navbar/ClientPortalNavbar";
import {
  isAdmin,
  isClient,
  isLoggedIn,
  logout,
} from "./services/accountService";
import { useHistory, useLocation } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import LoadingModal from "./components/LoadingModal";
import { pages, localStorageVariables } from "./constants";
import { tokenKeyName, refreshKeyName } from "./config";
import jwtDecode from "jwt-decode";
import handleApiError from "./helpers/handleApiError";
import { CopyrightDisplay } from "./components/CopyrightDisplay";
import { getCDKSubscriptionStatus } from "./services/cdkOrderService";
import { isEmpty } from "lodash";
import { ClientRouteManager } from "./Routers/ClientRouteManager";
import { PublicRouteManager } from "./Routers/PublicRouteManager";
import { LoadingSpinner } from "./components/Utility/LoadingSpinner";
import Announcement from "./components/Announcement";
import useLocalStorage from "./hooks/useLocalStorage";
import { getCacheBustVer as getCacheBustDate } from "./services/notificationService";
import { useQuery } from "@tanstack/react-query";
import moment from "moment";
// import { AdminSite } from "./components/Admin/AdminSite";

const AdminSite = React.lazy(() =>
  import("./components/Admin/AdminSite").then((module) => {
    return { default: module.AdminSite };
  }),
);
// import { CDKPromoPage } from "./components/CDK/CDKSignupPage/CDKPromoPage_Old1";

const LogoutHandler = () => {
  const history = useHistory();
  useEffect(() => {
    const handleInvalidToken = (e) => {
      console.log("Logout Handler Event", e);
      //if the event key is for the access token, and there was a value previously and now there is no value. So, basically when the access token is deleted.
      if (e.key === tokenKeyName && e.oldValue && !e.newValue) {
        // Your logout logic here
        localStorage.removeItem(tokenKeyName);
        history.push(pages.loginPage.url);
        toast.error("Session Expired.");
      }
    };
    window.addEventListener("storage", handleInvalidToken);
    return function cleanup() {
      window.removeEventListener("storage", handleInvalidToken);
    };
  }, []);
  return null;
};

const CacheBuster = () => {
  const [lastCacheBustDate, setLastCacheBustVer] = useLocalStorage({
    key: localStorageVariables.cacheBustDate,
    initialValue: null,
  });

  const query = useQuery({
    queryKey: ["cacheBustDate"],
    queryFn: getCacheBustDate,
    refetchInterval: 5 * 60 * 1000, // Check every 5 minutes
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: false,
    meta: {
      disabledGlobalToast: true,
    },
  });

  useEffect(() => {
    //if the query failed, return without doing any other checks.
    if (!query.isSuccess) {
      return;
    }
    const currentCacheBustDate = query.data.data;
    // console.log(currentCacheBustVer);
    // console.log(lastCacheBustVer);
    // console.log("previous cache bust val", lastCacheBustVer);
    // console.log("current cache bust val", currentCacheBustVer);
    // console.log("current date time", moment());
    // console.log(
    // "current cache bust val is after current time",
    // moment().isAfter(moment(currentCacheBustVer))
    // );
    //If the current datetime is after the scheduled datetime for reloading AND we haven't already done the reload for this cache bust
    if (
      moment().isAfter(moment(currentCacheBustDate)) &&
      currentCacheBustDate !== lastCacheBustDate
    ) {
      setLastCacheBustVer(currentCacheBustDate);
      window.location.reload();
    }
  }, [query.data]);
  return null;
};

//has access check
function App() {
  const [loading, setLoading] = useState(false);
  const [lastLoginUpdate, setLastLoginUpdate] = useState(new Date());

  const setLoadingCallback = useCallback((val) => {
    // console.log(val);
    // console.log("WHO DID THIS");
    setLoading(val);
  }, []);

  const history = useHistory();
  const { pathname } = useLocation();

  const token = localStorage.getItem(tokenKeyName);
  const [decodedClientInfo, setDecodedClientInfo] = useState({});
  const [cdkSubscriptionStatus, setCDKSubscriptionStatus] = useState();

  //will refresh the decoded client token information whenever the token changes
  useEffect(() => {
    // console.log("this ran");
    //console.log("token has changed");
    //The following is for data we want to fetch on site load/refresh
    const fetchSiteloadData = async () => {
      try {
        if (isClient()) {
          const cdkSubscriptionStatusResponse =
            await getCDKSubscriptionStatus();
          setCDKSubscriptionStatus(cdkSubscriptionStatusResponse.data);
        }
      } catch (ex) {
        handleApiError(ex, history);
      }
    };
    try {
      if (token) {
        const decodedClientJWT = jwtDecode(token);
        if (!isEmpty(decodedClientJWT)) {
          fetchSiteloadData();
        }
        // console.log("hi from app.js");
        // console.log(decodedClientJWT);
        setDecodedClientInfo(decodedClientJWT);
      } else {
        setDecodedClientInfo({});
      }
    } catch (ex) {
      history.push(pages.loginPage.url);
      // toast.error(
      //   "Error parsing client information. Please try signing in again." //probably not needed as we only run this if there is a token to begin with. if we dont check to see if there is a token to begin with we get the error message whenever we go to the login page anyway as there is no token which is not wanted.
      // );
    }
  }, [token]);

  const [linkedAccounts, setLinkedAccounts] = useState([]);
  // console.log(decodedClientInfo);
  // console.log(isLoggedIn());
  // console.log(pathname);
  // console.log(history);

  const handleLogoutClick = (e) => {
    //reset global state on logout
    e.preventDefault();
    setLastLoginUpdate(new Date());
    setLinkedAccounts([]);
    logout();
    history.push(pages.loginPage.url);
  };

  return (
    <>
      <ToastContainer />
      {/* <CacheBuster /> */}
      {/* <LogoutHandler /> */}
      {isAdmin() ? (
        <Suspense
          fallback={
            <div
              style={{
                display: "grid",
                height: "100dvh",
                placeContent: "center",
              }}
            >
              <LoadingSpinner spinnerClassAppend="p-5 text-note" />
            </div>
          }
        >
          <AdminSite />
        </Suspense>
      ) : (
        <>
          {/* <ForLoopVsReduceTest /> */}
          <div
            className="tw:flex"
            style={{ position: "relative", minHeight: "100dvh" }}
          >
            <ClientPortalNavbar
              onLogoutClick={handleLogoutClick}
              lastLoginUpdate={lastLoginUpdate}
              decodedClientInfo={decodedClientInfo}
              setLoading={setLoadingCallback}
              linkedAccounts={linkedAccounts}
              setLinkedAccounts={setLinkedAccounts}
              cdkSubscriptionStatus={cdkSubscriptionStatus}
            />

            <Announcement />

            <div className="App footer-gap tw:flex-1">
              {/* Moved loading modal inside app as app class has 20px global padding applied and when any other modal that was inside and the loading modal showed at the same time they would not be aligned. */}
              <LoadingModal active={loading} />
              {/* removing for now, moved component into SessionManager.jsx */}
              {/* <SessionTimeoutModal
          decodedClientInfo={decodedClientInfo}
          history={history}
          pathname={pathname}
          setLoading={setLoadingCallback}
        /> */}
              {isLoggedIn() ? (
                <ClientRouteManager
                  loading={loading}
                  setLoadingCallback={setLoadingCallback}
                  linkedAccounts={linkedAccounts}
                  setLinkedAccounts={setLinkedAccounts}
                  decodedClientInfo={decodedClientInfo}
                  pathname={pathname}
                  cdkSubscriptionStatus={cdkSubscriptionStatus}
                  setCDKSubscriptionStatus={setCDKSubscriptionStatus}
                />
              ) : (
                <PublicRouteManager
                  setLoadingCallback={setLoadingCallback}
                  setLastLoginUpdate={setLastLoginUpdate}
                />
              )}
            </div>
            <CopyrightDisplay />
          </div>
        </>
      )}
    </>
  );
}

export default App;
